/** @jsx jsx */
import { useCallback, useEffect, useState } from 'react'
import { Box, Flex, Grid, jsx, Spinner, Text } from 'theme-ui'
import StoreLocatorList from '~/components/StoreLocator/List'
import StoreLocatorMap from '~/components/StoreLocator/Map'
import StoreLocatorSearch from '~/components/StoreLocator/Search'

// @see https://console.cloud.google.com/google/maps-apis/build/locator-plus
const StoreLocator = ({
  defaultCenter,
  defaultLocations,
  distanceThreshold = 100,
  mapId,
  styles,
  title,
  ui,
}) => {
  const [activeLocation, setActiveLocation] = useState(null)
  const [currentCenter, setCurrentCenter] = useState(null)
  const [error, setError] = useState(null)
  const [hasScriptLoaded, setHasScriptLoaded] = useState(false)
  const [locations, setLocations] = useState(
    defaultLocations.map((location, id) => ({ id, ...location }))
  )
  const [map, setMap] = useState(null)

  if (typeof window !== 'undefined') {
    window.storeLocatorInit = () => setHasScriptLoaded(true)
  }

  // Gets distance between coordinates and the current center.
  const getDistanceFromCurrentCenter = useCallback(
    (coords) =>
      window.google.maps.geometry.spherical.computeDistanceBetween(
        currentCenter,
        coords
      ) * 0.000621371,
    [currentCenter] // eslint-disable-line
  )

  useEffect(() => {
    setCurrentCenter(defaultCenter)
  }, [defaultCenter])

  useEffect(() => {
    const apiKey = process.env.GATSBY_GOOGLE_MAPS_KEY
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&callback=window.storeLocatorInit&v=weekly&libraries=places,geometry`
    script.async = true
    script.defer = true
    document.body.appendChild(script)
  }, []) // eslint-disable-line

  return (
    <Box sx={{ ...styles?.layout?.wrapper }}>
      {hasScriptLoaded && currentCenter && (
        <Grid
          columns={[1, null, 12]}
          sx={{
            gridTemplateRows: [null, null, 'min-content 1fr'],
            maxHeight: [null, null, '480px'],
            rowGap: [1, 2],
            ...styles?.layout?.grid,
          }}
        >
          <Box
            sx={{
              gridColumn: [null, null, '1 / 5'],
              gridRow: [null, null, '1 / 2'],
              ...styles?.layout?.search,
            }}
          >
            {ui?.title}
            <Box>
              <StoreLocatorSearch
                map={map}
                setCurrentCenter={setCurrentCenter}
                setError={(error) => setError(error)}
                styles={styles}
                ui={ui}
              />
            </Box>
            {error && (
              <Text as="p" mt={1} sx={styles?.error}>
                {error}
              </Text>
            )}
          </Box>
          <Box
            sx={{
              gridColumn: [null, null, '5 / -1'],
              gridRow: [null, null, '1 / -1'],
              ...styles?.layout?.map,
            }}
          >
            <StoreLocatorMap
              activeLocation={activeLocation}
              currentCenter={currentCenter}
              distanceThreshold={distanceThreshold}
              getDistanceFromCurrentCenter={getDistanceFromCurrentCenter}
              locations={locations}
              map={map}
              mapId={mapId}
              setActiveLocation={setActiveLocation}
              setCurrentCenter={setCurrentCenter}
              setMap={(map) => setMap(map)}
              styles={styles}
              ui={ui}
            />
          </Box>
          <Box
            sx={{
              backgroundColor: '#FFF',
              gridColumn: [null, null, '1 / 5'],
              gridRow: [null, null, '2 / -1'],
              maxHeight: ['288px', null, 'none'],
              overflowY: 'scroll',
              px: 2,
              ...styles?.layout?.list,
            }}
          >
            <StoreLocatorList
              activeLocation={activeLocation}
              currentCenter={currentCenter}
              defaultCenter={defaultCenter}
              distanceThreshold={distanceThreshold}
              error={error}
              getDistanceFromCurrentCenter={getDistanceFromCurrentCenter}
              locations={locations}
              setActiveLocation={setActiveLocation}
              setError={setError}
              setLocations={setLocations}
              styles={styles}
              ui={ui}
            />
          </Box>
        </Grid>
      )}
      {(!hasScriptLoaded || !currentCenter) && (
        <Flex
          sx={{
            alignItems: 'center',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <Spinner
            color="brandTertiary"
            size="160"
            sx={{
              '@media (prefers-reduced-motion)': {
                circle: {
                  animationIterationCount: 8,
                },
              },
            }}
          />
        </Flex>
      )}
    </Box>
  )
}

export default StoreLocator
